import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isThisYear,
} from 'date-fns'

import { parseCalendarDate } from './calendarDate'

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

const isDateValue = (value: string | Date) =>
  value instanceof Date || !Number.isNaN(Date.parse(value as string))

const isValidFormat = (date: Date | string) => {
  if (date instanceof Date) return date

  return (
    date.includes('T') && (date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(date))
  )
}

const normalizeToIso8601 = (date: Date | string) => {
  if (isValidFormat(date)) return date

  let normalized = (date as string).replace(' UTC', 'Z').replace(' ', 'T')

  if (!normalized.endsWith('Z')) {
    normalized += 'Z'
  }

  return normalized
}

const toDate = (date: string | Date): Date | null => {
  // A date-only string is a calendar date: parse it in local time so it is not
  // shifted back a day by being read as UTC midnight.
  if (typeof date === 'string' && CALENDAR_DATE.test(date)) {
    return parseCalendarDate(date)
  }

  if (date instanceof Date) return date
  if (!isDateValue(date)) return null

  return new Date(normalizeToIso8601(date))
}

export const formatDate = (date?: string | Date, formatStr = 'MM/dd/yyyy') => {
  if (!date) return ''

  const parsed = toDate(date)
  if (!parsed) return date as string

  return format(parsed, formatStr)
}

export const humanizeDate = (date?: string | Date) => {
  if (!date) return ''

  const parsed = toDate(date)
  if (!parsed) return date as string

  return format(parsed, isThisYear(parsed) ? 'MMM d' : 'MMM d, yyyy')
}

export const formatDateRange = (
  start: string | Date,
  end: string | Date,
  formatStr = 'MMM d, yyyy',
) => `${formatDate(start, formatStr)} – ${formatDate(end, formatStr)}`

// The same range with every repeated part said once: a range inside one year prints the year at the
// end only, and one inside a single month drops the second month name too. So "Apr 1, 2025 –
// Apr 30, 2025" reads as "Apr 1 – 30, 2025". A range that crosses a year keeps both years.
export const formatCompactDateRange = (
  start: string | Date,
  end: string | Date,
) => {
  const from = toDate(start)
  const to = toDate(end)
  if (!from || !to || from.getFullYear() !== to.getFullYear()) {
    return formatDateRange(start, end)
  }
  const isSameMonth = from.getMonth() === to.getMonth()
  return `${format(from, 'MMM d')} – ${format(to, isSameMonth ? 'd, yyyy' : 'MMM d, yyyy')}`
}

export const fromNow = (date?: Date | string) => {
  if (!date) return '0 days'

  const normalizedDate = normalizeToIso8601(date)
  return formatDistanceToNow(new Date(normalizedDate))
}

export const daysUntil = (date?: Date | string) => {
  if (!date) return Number.POSITIVE_INFINITY

  const normalizedDate = normalizeToIso8601(date)
  return differenceInCalendarDays(new Date(normalizedDate), new Date())
}
